/**
 * Team → Timesheets → Manager feedback (prototype tsFeedback). One line per person on how they could save
 * time, tied to the week or month on screen; the member sees it on their Today page.
 * Reads GET /timesheets/feedback; writes POST / DELETE /timesheets/feedback (timesheet.feedback.write — the
 * founder and the associate director, PA decision on t-60cf674e: the prototype wins). Anyone else who can read
 * the notes sees them without the write box. The intro names who can write from GET /users, never a literal.
 */
import { useState } from 'react';
import { FeedbackCategory, type TimesheetFeedback, type User } from '@amplispot/contracts';
import { capabilitiesFor } from '@amplispot/domain';
import { call, newIdempotencyKey } from '../../api';
import { useUsers } from '../../lib/data';
import { dayLabel, firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { fh, useFeedback, useTsWrite } from '../../lib/timesheets';
import { Avatar, ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import type { PersonAgg } from './TimesheetReport';

const CATS = FeedbackCategory.options;

/** "Week of 12 Oct" → "week of 12 Oct" inside a sentence (only the first letter drops). */
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** A saved-at timestamp → "Mon 5 Oct", on its Asia/Kolkata date (the prototype's `at`). */
const KOLKATA = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
const savedOn = (ts: string) => dayLabel(KOLKATA.format(new Date(ts)));

/** Prototype order: the associate director, then the founder ("<AD> and <founder> can both write"). */
const WRITER_ORDER: Record<string, number> = { ops_ad: 0, founder: 1 };

/** Active people who hold timesheet.feedback.write, by role or by a grant on top of it. */
function writersOf(users: User[]): string[] {
  return users
    .filter(
      (u) =>
        u.status === 'active' &&
        (capabilitiesFor(u.role).includes('timesheet.feedback.write') ||
          (u.granted_capabilities as readonly string[]).includes('timesheet.feedback.write')),
    )
    .sort((a, b) => (WRITER_ORDER[a.role] ?? 9) - (WRITER_ORDER[b.role] ?? 9) || a.name.localeCompare(b.name))
    .map((u) => firstName(u.name));
}

/** "A and B can both write." · "A can write." · "A, B and C can write." */
function whoCanWrite(names: string[]): string {
  if (!names.length) return '';
  if (names.length === 1) return `${names[0]} can write.`;
  if (names.length === 2) return `${names[0]} and ${names[1]} can both write.`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} can write.`;
}

export function TsFeedback({ period, A, canWrite }: { period: string; A: { ppl: PersonAgg[] }; canWrite: boolean }) {
  const fb = useFeedback();
  const { can } = useSession();
  const users = useUsers(can('users.read'));
  const writers = whoCanWrite(writersOf(users.data ?? []));
  const toast = useToast();
  const [text, setText] = useState<Record<string, string>>({});
  const [cat, setCat] = useState<Record<string, FeedbackCategory>>({});
  const create = useTsWrite((v: { userId: string; body: string; category: FeedbackCategory }) =>
    call('feedback.create', { body: { user_id: v.userId, period, category: v.category, body: v.body }, idempotencyKey: newIdempotencyKey() }),
  );
  const del = useTsWrite((id: string) => call('feedback.delete', { params: { id } }));

  if (fb.isLoading) return <Loading />;
  if (fb.error) return <ErrorState error={fb.error} onRetry={() => void fb.refetch()} />;
  const all = fb.data ?? [];
  const list = all.filter((f) => f.period === period);
  const others = all.filter((f) => f.period !== period).slice(0, 6);

  const save = (p: PersonAgg) => {
    const t = (text[p.user.id] ?? '').trim();
    if (!t) return toast('Write a line of feedback first');
    if (t.length < 10) return toast('Say how they could save time, in a sentence or two');
    create.mutate(
      { userId: p.user.id, body: t, category: cat[p.user.id] ?? 'Template' },
      {
        onSuccess: () => {
          toast(`Feedback saved for ${firstName(p.user.name)}. They will see it on their Today page.`);
          setText((x) => ({ ...x, [p.user.id]: '' }));
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  };

  const card = (f: TimesheetFeedback) => (
    <div className="fbk" key={f.id}>
      <div className="split" style={{ gap: 10 }}>
        <Avatar user={f.user} />
        <b>{firstName(f.user.name)}</b>
        <span className="chip">{f.category}</span>
        <div className="grow" />
        <span className="sub" style={{ fontSize: 12 }}>
          {firstName(f.by.name)}, {savedOn(f.at)}
        </span>
        {canWrite ? (
          <button
            className="btn sm ghost"
            disabled={del.isPending}
            onClick={() => del.mutate(f.id, { onSuccess: () => toast('Feedback removed'), onError: (e) => toast(errorText(e)) })}
          >
            Remove
          </button>
        ) : null}
      </div>
      <p>{f.body}</p>
    </div>
  );

  return (
    <>
      <div className="sub" style={{ maxWidth: 700, margin: '0 0 18px', fontSize: 13.5 }}>
        {canWrite
          ? `Write one line on how a person could save time, tied to ${lowerFirst(period)}. They see it on their Today page.${writers ? ` ${writers}` : ''}`
          : `Notes on how each person could save time, for ${lowerFirst(period)}. They see them on their Today page.`}
      </div>
      <div className="cols c2" style={{ gap: 22, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          {A.ppl.map((p) => (
            <div className="panel" key={p.user.id}>
              <div className="bd">
                <div className="split" style={{ gap: 12, marginBottom: canWrite ? 12 : 0 }}>
                  <Avatar user={p.user} />
                  <div>
                    <b>{firstName(p.user.name)}</b>
                    <div className="sub" style={{ fontSize: 12.5 }}>
                      {fh(p.logged)} logged, {fh(p.gap)} not logged, {p.late} late, {p.missing} not filed
                    </div>
                  </div>
                </div>
                {canWrite ? (
                  <>
                    <textarea
                      className="in"
                      rows={2}
                      placeholder={`How could ${firstName(p.user.name)} save time?`}
                      style={{ marginBottom: 10 }}
                      value={text[p.user.id] ?? ''}
                      onChange={(e) => setText((x) => ({ ...x, [p.user.id]: e.target.value }))}
                      aria-label={`Feedback for ${firstName(p.user.name)}`}
                    />
                    <div className="split" style={{ gap: 10 }}>
                      <select
                        className="in"
                        style={{ width: 'auto', borderRadius: 99 }}
                        value={cat[p.user.id] ?? 'Template'}
                        onChange={(e) => setCat((x) => ({ ...x, [p.user.id]: e.target.value as FeedbackCategory }))}
                        aria-label="Category"
                      >
                        {CATS.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                      <div className="grow" />
                      <button className="btn sm pri" onClick={() => save(p)} disabled={create.isPending}>
                        Save feedback
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="lbl" style={{ margin: '0 0 10px' }}>
            Feedback for {period}
          </div>
          <div style={{ display: 'grid', gap: 12 }}>{list.length ? list.map(card) : <div className="sub">Nothing written yet for this period.</div>}</div>
          {others.length ? (
            <>
              <div className="lbl" style={{ margin: '24px 0 10px' }}>
                Earlier feedback
              </div>
              <div style={{ display: 'grid', gap: 12 }}>{others.map(card)}</div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
